import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import LangToggle from "./LangToggle";
import { useTranslation } from "../../i18n/LanguageContext";

export default function Navigation({ onQuoteClick }) {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        open
          ? "bg-[#f5effb] border-b border-[#d9cde0]"
          : scrolled
            ? "glass-strong border-b border-white/5"
            : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
        <a
          href="#fooldal"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 sm:gap-3.5 group min-w-0"
        >
          <img
            src="/logo.png"
            alt={t.nav.brand}
            className="h-10 sm:h-12 md:h-14 w-auto object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.3)] group-hover:drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)] transition-all duration-500"
          />
          <span className="flex flex-col leading-none min-w-0">
            <span className="font-brand text-lg sm:text-xl md:text-[22px] uppercase gold-text">
              {t.nav.brand}
            </span>
            <span className="font-brand-sub text-[9px] sm:text-[10px] md:text-[11px] uppercase text-[#0a0a0a]/90 mt-1">
              {t.nav.brandSub}
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {t.nav.links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-sm tracking-wide text-[#594f5a] hover:text-[#1a1a1a] transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute -bottom-2 left-0 w-0 h-px bg-[#0a0a0a] group-hover:w-full transition-all duration-500" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <LangToggle compact />
          <button
            data-testid="nav-quote-btn"
            onClick={onQuoteClick}
            className="hidden md:inline-flex btn-gold px-5 py-2.5 rounded-full text-sm"
          >
            {t.nav.cta}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-[#1a1a1a]"
            aria-label={t.nav.menuAria}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden fixed inset-x-0 top-20 bottom-0 z-40 bg-[#f5effb] overflow-y-auto"
        >
          <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col gap-1">
            {t.nav.links.map((l, i) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between py-4 border-b border-[#d9cde0] text-lg font-serif-display text-[#1a1a1a] hover:text-[#0a0a0a] transition-colors"
              >
                <span>{l.label}</span>
                <span className="text-xs tracking-[0.22em] text-[#8a7f8f]">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </a>
            ))}
            <div className="pt-6">
              <button
                data-testid="nav-quote-btn-mobile"
                onClick={() => {
                  setOpen(false);
                  onQuoteClick();
                }}
                className="btn-gold w-full px-5 py-3 rounded-full text-sm"
              >
                {t.nav.cta}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
